/**
 * Unified assistant turn grouping + rendering.
 *
 * One assistant run used to surface in the chat as a sequence of separate
 * "message" bubbles — the user's prompt, then a transient "I'll check…"
 * assistant line, then a sequence of `toolResult`-shaped messages (one per
 * exec call), then the final answer. That made the chat hard to read.
 *
 * The render model introduced here flattens a single assistant run into
 * exactly one visual turn:
 *
 *     user prompt
 *     ── assistant turn ─────────────────────────
 *        ▸ Activity · 4 steps · 2.3s     (collapsible)
 *            expanded body shows each tool / status / command output
 *        Final answer (always visible outside the activity section)
 *     ── next user prompt ──
 *
 * The chat still keeps every underlying event (tools, outputs, errors,
 * timestamps, subagent cards, etc.); only the visual presentation
 * collapses them.
 *
 * Lifecycle invariant (chat message lifecycle around activity/tool
 * execution and final summaries):
 *
 *     User request
 *       → Assistant activity starts
 *         → Tools / commands / agent execution
 *           → Optional internal verification (e.g. `fleet_todo_read`)
 *             → Activity closes
 *               → Final assistant summary (rendered as a normal
 *                 assistant message, OUTSIDE the Activity box)
 *                 → Optional next-action question
 *
 * Post-completion assistant text emitted by the agent's heartbeat poll
 * (e.g. the "Are you sure no work needs to be done?" idle challenge) is a
 * heartbeat artifact, NOT the user's final summary. Such tail messages
 * are skipped when picking the canonical `finalAnswer` below, and are
 * additionally stripped from chat-visible transcripts by
 * `chat-transcript-filters.ts::shouldOmitChatTranscriptMessage` so they
 * never surface inside the Activity box or anywhere else.
 */
import { html, nothing, type TemplateResult } from "lit";
import { isHeartbeatIdleChallenge } from "../../../../src/auto-reply/heartbeat.ts";
import type { AssistantIdentity } from "../assistant-identity.ts";
import { icons } from "../icons.ts";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import { resolveLiveThinkingSummary } from "./chat-activity-collect.ts";
import {
  buildLiveRunSteps,
  buildLiveRunStepsWithSticky,
  hasActiveToolInStream,
  type LiveRunStep,
} from "./chat-live-run.ts";
import { renderChatLiveRunGroup } from "./chat-live-run.ts";
import { formatChatWaitElapsed } from "./chat-wait-elapsed.ts";
import { isCollapsibleText, renderAvatar, renderGroupedMessage } from "./grouped-render.ts";
import type { MessageActionHandlers } from "./message-actions.ts";
import { extractText, extractThinking } from "./message-extract.ts";
import { normalizeMessage } from "./message-normalizer.ts";
import type { MessageRewindTarget } from "./message-transcript.ts";

/**
 * Per-turn sticky hint memo for the live-run step chip.
 *
 * The live-run renderer is a pure function called on every render, but
 * the underlying `liveRunHint` prop is not stable: the gateway emits
 * `null` between phases (tool-end → next tool-start), so the chip would
 * flash `null → "Running X…" → null` (JAISIU-3153). We latch the first
 * non-empty hint per stable `turnKey` and only replace it on a
 * *different* non-empty hint.
 *
 * The map is keyed by the **session-stable** `turn.key` string rather
 * than the `AssistantTurn` object. Every `chat.history` poll rebuilds
 * the items array via `buildAssistantTurns`, so each poll produces a
 * fresh `AssistantTurn` object — keying a `WeakMap` by that object
 * silently evicts on every poll and the sticky flickers back to `null`
 * (the regression that brought this fix). Keying by the stable
 * `turn.key` (a `turn:<id>` / `turn:live:<startedAt>` string) survives
 * object churn across renders.
 *
 * Bounded by a small LRU (default 128 entries) so long-running sessions
 * do not leak. Entries are evicted in insertion order, which is close
 * enough to LRU for our access pattern (re-renders re-touch the same
 * key) and avoids the bookkeeping cost of a real LRU.
 */
const STICKY_HINT_MAX_ENTRIES = 128;
const liveRunStickyByTurn = new Map<string, string | null>();

/**
 * JAISIU-3752 — parallel per-turn memo for the live thinking summary.
 * Same LRU eviction rules as `liveRunStickyByTurn` (above) so the two
 * stickies share the same retention window. The two maps are tracked
 * independently so a liveRunHint change (e.g. "Web search…") does not
 * bump the thinking summary sticky and vice-versa.
 */
const liveRunThinkingSummaryByTurn = new Map<string, string | null>();

function rememberStickyHint(key: string, hint: string | null): void {
  // Re-insert to bump the entry to the most-recent end of the iteration
  // order, so eviction drops the truly cold entries first.
  if (liveRunStickyByTurn.has(key)) {
    liveRunStickyByTurn.delete(key);
  }
  liveRunStickyByTurn.set(key, hint);
  while (liveRunStickyByTurn.size > STICKY_HINT_MAX_ENTRIES) {
    const oldest = liveRunStickyByTurn.keys().next().value;
    if (oldest === undefined) break;
    liveRunStickyByTurn.delete(oldest);
  }
}

function rememberStickyThinkingSummary(key: string, summary: string | null): void {
  if (liveRunThinkingSummaryByTurn.has(key)) {
    liveRunThinkingSummaryByTurn.delete(key);
  }
  liveRunThinkingSummaryByTurn.set(key, summary);
  while (liveRunThinkingSummaryByTurn.size > STICKY_HINT_MAX_ENTRIES) {
    const oldest = liveRunThinkingSummaryByTurn.keys().next().value;
    if (oldest === undefined) break;
    liveRunThinkingSummaryByTurn.delete(oldest);
  }
}

/** A single entry inside the activity section of an assistant turn. */
export type AssistantTurnActivityEntry = {
  /** Stable id (used by Lit's repeat key for stable DOM diffing). */
  id: string;
  /** One-line label shown in the activity card body. */
  label: string;
  /** Optional secondary line (tool args, command snippet, etc.). */
  detail?: string;
  /** Underlying raw message that produced this entry (used to render full
   *  tool cards / outputs when the user expands the activity section). */
  message: unknown;
  /** When true the entry is still running; rendered with a spinner. */
  active: boolean;
  /** Optional source tag (e.g. "Subagent · worker" or "Session"). */
  source?: string;
};

/**
 * One logical assistant turn between two user prompts (or the start of the
 * transcript). Contains the activity stream and the final assistant answer.
 */
export type AssistantTurn = {
  kind: "assistant-turn";
  /** Stable key — same id is used as the `<div>` element key for Lit. */
  key: string;
  /** All non-final messages and tool events belonging to this turn. */
  activity: AssistantTurnActivityEntry[];
  /**
   * The canonical assistant answer for this turn — the last assistant-role
   * message whose `extractText` is non-empty, *excluding* heartbeat idle-
   * challenge tails emitted by the agent's heartbeat poll. `null` while
   * the turn is still running OR when the agent never produced a final
   * answer.
   *
   * Heartbeat-tail suppression invariant: when the agent finishes the real
   * user-requested work and a heartbeat poll arrives afterwards, the agent
   * may emit the literal challenge `Are you sure no work needs to be
   * done?` as its last assistant text in the turn. That message is a
   * heartbeat-poll artifact and MUST NOT be promoted to the user-facing
   * `finalAnswer` — we prefer the most recent non-heartbeat-tail assistant
   * text instead, so the Activity box's "final" bubble always carries the
   * agent's real reply. The heartbeat tail is also stripped from chat-
   * visible transcripts by `chat-transcript-filters.ts` (see
   * `shouldOmitChatTranscriptMessage`), so it never reaches the user
   * surface in any rendering path.
   */
  finalAnswer: unknown | null;
  /** Stable id for the final-answer message if known. */
  finalAnswerId?: string;
  /** True while the agent is still working on this turn. */
  isLive: boolean;
  /**
   * Stable runId for this turn — used to identify whether this turn is the
   * one currently streaming. Populated for turns whose `currentRunId`
   * matches the live run; older turns carry `null` and render as
   * `isLive: false` regardless of the global live indicator.
   */
  runId?: string | null;
  /** Wall-clock start of the turn (first non-user event timestamp). */
  startedAt: number;
  /** Wall-clock end of the turn — `null` while `isLive`. */
  completedAt: number | null;
  /** Subagent cards observed during this turn (used to render the live
   *  activity strip while the turn is still running). */
  liveSteps?: LiveRunStep[];
  /** Streaming reasoning text (only populated while `isLive`). */
  liveReasoningText?: string;
  /** Streaming assistant text (only populated while `isLive`). */
  liveStreamText?: string;
};

export type BuildAssistantTurnsOptions = {
  /**
   * While the chat is mid-run, `views/chat.ts` passes the synthetic
   * `kind: "live-run"` item plus the in-flight tool messages. We merge them
   * into the trailing `assistant-turn` and flag it as `isLive`.
   */
  isLive: boolean;
  /**
   * The chat controller's currently-tracked runId, if any. Only the trailing
   * turn whose runId matches this value is rendered as `isLive: true`.
   * Older turns render as `isLive: false` regardless of the global
   * `isLive` flag, so a stale streaming state never paints every historical
   * assistant bubble into "thinking / generating response".
   */
  currentRunId?: string | null;
  /**
   * Optional clock for missing timestamps. Must NOT be used as
   * `completedAt` for finished turns — that made activity durations keep
   * growing on every wait-ticker re-render.
   */
  now?: number;
};

/**
 * Pure grouping step. Takes the raw items returned by `buildChatItems` and
 * collapses every run of non-user items into one `AssistantTurn`. Items
 * that are user-shape (user role), file-changes, subagent-run,
 * or orchestration pass through unchanged.
 */
export function buildAssistantTurns(
  items: Array<ChatItem | MessageGroup>,
  opts: BuildAssistantTurnsOptions,
): Array<ChatItem | MessageGroup | AssistantTurn> {
  const now = opts.now ?? Date.now();
  const result: Array<ChatItem | MessageGroup | AssistantTurn> = [];

  let buffer: Array<ChatItem | MessageGroup> = [];
  // Track which buffer corresponds to the trailing assistant turn that
  // owns the live-run item. The flag flips to true as soon as we encounter
  // a `live-run` item while walking the input list, and stays true until
  // we hit a boundary — so exactly one turn (the current one) is marked
  // as the live run. Older turns render as `isLive: false` regardless of
  // the global flag.
  let bufferHoldsLiveRun = false;
  // Index of the trailing assistant turn inside `result`. Older turns
  // are forced to `isLive: false` after the walk completes, regardless
  // of the global `isLive` flag — this is what stops a stale live-run
  // indicator from painting the whole transcript into "thinking".
  let trailingTurnIndex = -1;
  const flush = () => {
    if (buffer.length === 0) {
      bufferHoldsLiveRun = false;
      return;
    }
    const turn = collapseBufferToTurn(buffer, {
      // Live status is decided after the walk so we can guarantee that
      // only the trailing turn inherits `opts.isLive`. Older turns are
      // forced to `isLive: false` below. `runId` is set here only when
      // the buffer owned the streaming `live-run` item.
      isLive: false,
      now,
      runId: bufferHoldsLiveRun ? (opts.currentRunId ?? null) : null,
    });
    if (turn) {
      trailingTurnIndex = result.length;
      result.push(turn);
    } else {
      // Defensive fallback — never lose items.
      for (const item of buffer) {
        result.push(item);
      }
    }
    buffer = [];
    bufferHoldsLiveRun = false;
  };

  for (const item of items) {
    if (isBoundary(item)) {
      flush();
      result.push(item);
      continue;
    }
    if ("kind" in item && item.kind === "live-run") {
      // Mark this buffer as the trailing live run. The buffer will be
      // collapsed into the assistant turn that owns the streaming reply.
      bufferHoldsLiveRun = true;
    }
    buffer.push(item);
  }
  flush();

  // Per-turn liveness: only the trailing assistant turn is live. A stale
  // global `isLive` (caused by latched streaming state upstream) MUST NOT
  // flip older completed turns into "thinking / generating response".
  if (trailingTurnIndex >= 0 && opts.isLive) {
    const trailing = result[trailingTurnIndex];
    if (trailing && trailing.kind === "assistant-turn") {
      trailing.isLive = true;
      // `collapseBufferToTurn` populated `completedAt` while it was
      // building the turn with `isLive: false`. Reset it now that the
      // turn is the live one — otherwise the UI shows a finished
      // duration for a turn that is still in flight.
      trailing.completedAt = null;
      // Stamp the current runId on the trailing turn so downstream code
      // can identify it as the in-flight run. Even when no `live-run`
      // item was present in the buffer (e.g. sending started with an
      // empty transcript), the trailing turn still owns the live run.
      if (trailing.runId == null && opts.currentRunId) {
        trailing.runId = opts.currentRunId;
      }
      // JAISIU-activity-always-visible: while the turn is still
      // mid-run, the LAST activity row keeps its loader icon (active
      // state) so the operator can tell the run is still in flight at
      // a glance — instead of every row showing a checkmark and
      // looking already-done while the next delta hasn't landed yet.
      // Older rows stay as completed history so the timeline reads
      // correctly.
      if (trailing.activity.length > 0) {
        const last = trailing.activity[trailing.activity.length - 1];
        if (last) {
          last.active = true;
        }
      }
    }
  }
  return result;
}

/**
 * Boundary items split the chat into assistant turns but render as their
 * own rows. Only assistant/tool-role items and the synthetic `live-run`
 * item get folded into an `AssistantTurn`.
 *
 * Production callers (`views/chat.ts`) wrap runs of same-role messages in
 * `MessageGroup` (kind === "group") before passing them here, so we MUST
 * treat assistant/tool `MessageGroup` items as foldable; otherwise the chat
 * stays as 73 separate `.chat-group.assistant` rows with no collapse. Only
 * user/system groups render as their own boundary.
 */
function isBoundary(item: ChatItem | MessageGroup): boolean {
  if ("kind" in item) {
    if (item.kind === "group") {
      const role = item.role?.toLowerCase();
      return role === "user" || role === "system";
    }
    if (item.kind === "message") {
      const role = (item.message as { role?: string }).role?.toLowerCase();
      return role === "user" || role === "system";
    }
    return item.kind !== "live-run";
  }
  return false;
}

function collapseBufferToTurn(
  buffer: Array<ChatItem | MessageGroup>,
  opts: { isLive: boolean; now: number; runId?: string | null },
): AssistantTurn | null {
  if (buffer.length === 0) {
    return null;
  }

  let startedAt = Number.POSITIVE_INFINITY;
  let lastAt = 0;
  let lastAssistantText: unknown = null;
  let lastAssistantIndex = -1;

  const allMessages: Array<{
    idx: number;
    message: unknown;
    text: string;
    role: string;
    timestamp: number;
    isFinalAnswer: boolean;
  }> = [];
  let liveRunItem: Extract<ChatItem, { kind: "live-run" }> | null = null;
  let minimalStatusItem: Extract<ChatItem, { kind: "minimal-status" }> | null = null;
  let messageIndex = 0;

  for (const item of buffer) {
    if ("kind" in item && item.kind === "live-run") {
      liveRunItem = item;
      lastAt = Math.max(lastAt, item.startedAt);
      startedAt = Math.min(startedAt, item.startedAt);
      continue;
    }
    if ("kind" in item && item.kind === "minimal-status") {
      minimalStatusItem = item;
      startedAt = Math.min(startedAt, item.startedAt);
      lastAt = Math.max(lastAt, item.startedAt);
      continue;
    }
    const messages = "messages" in item ? item.messages : null;
    const flat = messages
      ? messages.map((m) => m.message)
      : item.kind === "message"
        ? [item.message]
        : [];
    for (const message of flat) {
      const text = extractText(message)?.trim() ?? "";
      const normalized = normalizeMessage(message);
      const role = normalized.role?.toLowerCase() ?? "";
      const ts = normalized.timestamp || 0;
      startedAt = Math.min(startedAt, ts || startedAt);
      lastAt = Math.max(lastAt, ts);
      // Track the latest assistant text message — only the LAST assistant
      // text becomes the canonical final answer for the turn. Earlier
      // assistant messages are still kept in `allMessages` and surface in
      // the collapsible activity section so the user can see what the
      // agent was saying mid-run (this satisfies the "execution details
      // retained" acceptance criterion).
      if (Boolean(text) && role === "assistant") {
        lastAssistantText = message;
        lastAssistantIndex = messageIndex;
      }
      allMessages.push({
        idx: messageIndex,
        message,
        text,
        role,
        timestamp: ts,
        isFinalAnswer: false,
      });
      messageIndex += 1;
    }
  }

  // Now that the full buffer has been walked, identify the canonical
  // user-facing final answer.
  //
  // Chat message lifecycle (JAISIU-XXXX — activity vs final summary):
  //   1. Agent performs the user-requested work (exec / read / write).
  //   2. Agent emits its real summary ("Done — removed the trigger.").
  //   3. A heartbeat poll arrives (the system prompt instructs the
  //      agent to scan for open work). The poll chain typically looks
  //      like:
  //        a. assistant: "Let me scan for actual open work before
  //           deciding."
  //        b. tool:      fleet_todo_read / memory_search / etc.
  //        c. assistant: "Scan complete. Empty fleet todos, no active
  //           subagents, no memory blockers, only scheduled crons."
  //        d. assistant: "Are you sure no work needs to be done?"
  //      Items (a)-(d) are heartbeat-poll artifacts. They MUST NOT
  //      surface as the user-facing reply — they belong in the
  //      Activity box (collapsible, inspect-only) so the user can see
  //      what the agent did, while the canonical `finalAnswer`
  //      carries the agent's real reply (item 2 above).
  //
  // Detection: walk the message list backwards from the tail. The
  // heartbeat tail block begins at the first message that is either
  // (i) an assistant message matching `isHeartbeatIdleChallenge`,
  // (ii) an assistant message whose text matches the heartbeat-poll
  // narrative patterns ("Let me scan…", "Let me check…", "Scan
  // complete…", etc.), or (iii) a tool message whose name is one of
  // the heartbeat-poll tool names. Walk back over any tool calls,
  // tool results, and heartbeat-narrative assistant messages until we
  // hit a non-heartbeat assistant message — that is the canonical
  // `finalAnswer`.
  //
  // Falls back to the raw last assistant text only when the buffer
  // contains NO heartbeat-tail markers at all (the common case where
  // the agent simply replied without a heartbeat poll interleaving).
  const heartbeatTailEndIdx = findHeartbeatTailEnd(allMessages);
  const heartbeatTailStartIdx = findHeartbeatTailStart(allMessages, heartbeatTailEndIdx);
  let finalAnswerIndex = lastAssistantIndex;
  if (heartbeatTailStartIdx >= 0 && heartbeatTailStartIdx <= lastAssistantIndex) {
    // Walk backwards from the message just before the heartbeat tail
    // start, looking for the last assistant text message — that is the
    // real summary.
    for (let i = heartbeatTailStartIdx - 1; i >= 0; i -= 1) {
      const entry = allMessages[i];
      if (!entry) {
        continue;
      }
      if (entry.role === "assistant" && entry.text) {
        finalAnswerIndex = i;
        break;
      }
    }
  }
  const finalAnswerMessage =
    finalAnswerIndex >= 0 && finalAnswerIndex < allMessages.length
      ? allMessages[finalAnswerIndex]?.message
      : lastAssistantText;
  if (finalAnswerIndex >= 0 && allMessages[finalAnswerIndex]) {
    allMessages[finalAnswerIndex] = {
      ...allMessages[finalAnswerIndex],
      isFinalAnswer: true,
    };
  }

  // Build activity entries: every message that isn't the final answer, in
  // the order they originally appeared. Skip messages with no extractable
  // text and no tool cards — they're empty noise.
  const activityEntries: AssistantTurnActivityEntry[] = [];
  for (const entry of allMessages) {
    if (entry.isFinalAnswer) {
      continue;
    }
    const label = activityLabelFor(entry);
    if (!label) {
      continue;
    }
    activityEntries.push({
      // Stable id: timestamp when present, plus the global index in the
      // assistant turn. Falls back to index when the timestamp is missing
      // or zero. The previous form collapsed on duplicate timestamps —
      // e.g. three `process.poll` rows sharing `ts=1789046608470` all
      // got `id="act:1789046608470:42"` and Lit's repeat() unmounted
      // them on re-render, making the activity section look like it
      // was "randomly collapsing".
      id: `act:${entry.timestamp || 0}:${entry.idx}:${activityEntries.length}`,
      label,
      // Only assistant rows risk the double print: for those `label` is the
      // full text (`activityLabelFor` falls through to `text`) and
      // `renderActivityEntryMessage` renders `message` right below it, so a
      // `detail` repeated the same sentence twice in one row. Tool rows keep
      // their detail — their label is just `Running <name>…`.
      detail: isAssistantRole(entry.role)
        ? undefined
        : entry.text && entry.text !== label
          ? entry.text
          : undefined,
      message: entry.message,
      active: false,
      source: labelSourceFor(entry.role),
    });
  }
  // JAISIU-activity-always-visible: the last-entry-active flag is
  // applied AFTER the trailing-turn `isLive` patch below, so a single
  // source of truth (`opts.isLive`) drives both the bubble liveness
  // and the row-state. See the block right before `return result`.

  // Final-answer metadata. Prefer `finalAnswerMessage` (the user-facing
  // non-heartbeat-tail assistant text) — see comment above `finalAnswerIndex`.
  const finalAnswerRecord =
    finalAnswerMessage && typeof finalAnswerMessage === "object"
      ? (finalAnswerMessage as Record<string, unknown>)
      : null;
  const finalAnswerId =
    typeof finalAnswerRecord?.id === "string"
      ? finalAnswerRecord.id
      : typeof finalAnswerRecord?.transcriptId === "string"
        ? finalAnswerRecord.transcriptId
        : undefined;
  const turnKey = finalAnswerId
    ? `turn:${finalAnswerId}`
    : liveRunItem
      ? `turn:live:${liveRunItem.startedAt}`
      : `turn:${Number.isFinite(startedAt) ? startedAt : opts.now}`;

  if (!Number.isFinite(startedAt)) {
    startedAt = opts.now;
  }
  // Freeze completion on message timestamps only. Using wall-clock `now`
  // here made "Activity · Ns" keep climbing whenever chatWaitNow re-rendered
  // the thread (every ~200ms while any run was live, or after a stuck ticker).
  const completedAt = opts.isLive ? null : Math.max(lastAt, startedAt);

  return {
    kind: "assistant-turn",
    key: turnKey,
    activity: activityEntries,
    // Use the user-facing final answer (last non-heartbeat-tail assistant
    // text) so the Activity box's "final" bubble carries the agent's real
    // summary, not the post-completion heartbeat idle challenge.
    finalAnswer: finalAnswerMessage,
    finalAnswerId,
    isLive: opts.isLive,
    runId: opts.runId ?? null,
    startedAt,
    completedAt,
    liveReasoningText: liveRunItem?.reasoningStreamText,
    liveStreamText: liveRunItem?.streamText,
  };
}

function activityLabelFor(entry: { message: unknown; text: string; role: string }): string {
  const role = entry.role;
  const text = entry.text;
  const m = entry.message as Record<string, unknown> | null;
  if (role === "toolresult" || role === "tool" || role === "tool_result") {
    const name =
      typeof m?.toolName === "string"
        ? m.toolName
        : typeof m?.tool_name === "string"
          ? m.tool_name
          : "tool";
    return `Running ${name}…`;
  }
  if (typeof m?.toolCallId === "string" || typeof m?.tool_call_id === "string") {
    const name =
      typeof m.toolName === "string"
        ? m.toolName
        : typeof m.tool_name === "string"
          ? m.tool_name
          : "tool";
    return `Running ${name}…`;
  }
  if (text) {
    return text.length > 140 ? `${text.slice(0, 139).trim()}…` : text;
  }
  return "";
}

function isAssistantRole(role: string): boolean {
  return role.toLowerCase() === "assistant";
}

function labelSourceFor(role: string): string {
  if (role === "toolresult" || role === "tool" || role === "tool_result") {
    return "Tool";
  }
  return "Assistant";
}

/**
 * Heuristics for detecting the post-completion heartbeat-poll tail.
 *
 * The agent emits the literal idle challenge ("Are you sure no work
 * needs to be done?") whenever its heartbeat poll returns no
 * actionable items. The poll chain in the message buffer typically
 * looks like:
 *
 *   assistant: "Let me scan for actual open work before deciding."
 *   tool:      fleet_todo_read
 *   assistant: "Scan complete. Empty fleet todos, no active
 *               subagents, no memory blockers, only scheduled crons."
 *   assistant: "Are you sure no work needs to be done?"
 *
 * The whole chain is a heartbeat artifact and must NOT carry the
 * user-facing `finalAnswer`. We detect it with two helpers:
 *
 *   - `findHeartbeatTailEnd` — index of the trailing idle challenge
 *     (or -1 if no challenge is present).
 *   - `findHeartbeatTailStart` — walk backwards from the end of the
 *     tail over tool calls / tool results / heartbeat-narrative
 *     assistant messages until we hit a non-heartbeat message. The
 *     message just before that point is the real summary.
 *
 * The patterns are intentionally narrow to avoid mis-classifying
 * ordinary assistant prose. If we ever expand the agent's heartbeat
 * vocabulary, update `isHeartbeatTailAssistantText` and
 * `HEARTBEAT_POLL_TOOL_NAMES` below.
 */
const HEARTBEAT_POLL_TOOL_NAMES = new Set([
  "fleet_todo_read",
  "fleet_todos",
  "fleet_todo",
  "session_list",
  "subagents_list",
  "subagent_list",
  "memory_search",
  "memory_compute",
]);

/**
 * Regex matchers for assistant prose that signals the start of a
 * heartbeat-poll narration block. Each entry is anchored on a verb
 * that the agent uses to describe the scan ("Let me scan…", "Let me
 * check…", "Scan complete…", "Looking for open work…").
 */
const HEARTBEAT_NARRATIVE_PATTERNS: RegExp[] = [
  /^\s*let me scan\b/i,
  /^\s*let me check\b.*\b(work|todo|tasks?|subagents?|sessions?|open\s+work)\b/i,
  /^\s*let me look\b.*\b(work|todo|tasks?|subagents?|sessions?|open\s+work)\b/i,
  /^\s*scan(?:ning)?\s+for\b.*\b(work|todo|tasks?|subagents?|sessions?|open\s+work)\b/i,
  /^\s*scan\s+complete\b/i,
  /^\s*scanned\b.*\b(work|todo|tasks?|subagents?|sessions?)\b/i,
  /^\s*checking\s+for\s+(?:open\s+)?work\b/i,
  /^\s*looking\s+for\s+(?:open\s+)?work\b/i,
  /^\s*no\s+(?:open\s+)?work\b/i,
];

function isHeartbeatTailAssistantText(text: string): boolean {
  if (!text) {
    return false;
  }
  const trimmed = text.trim();
  if (isHeartbeatIdleChallenge(trimmed)) {
    return true;
  }
  return HEARTBEAT_NARRATIVE_PATTERNS.some((re) => re.test(trimmed));
}

function getToolNameFromMessage(message: unknown): string {
  const m = message as Record<string, unknown> | null;
  if (!m) {
    return "";
  }
  if (typeof m.toolName === "string") {
    return m.toolName;
  }
  if (typeof m.tool_name === "string") {
    return m.tool_name;
  }
  return "";
}

/**
 * Index of the trailing idle-challenge message in `messages`, or -1
 * when no such message exists. Used as the anchor point for the
 * heartbeat-tail walk-back.
 */
function findHeartbeatTailEnd(
  messages: Array<{ role: string; text: string; message: unknown }>,
): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const entry = messages[i];
    if (!entry) {
      continue;
    }
    if (entry.role === "assistant" && isHeartbeatIdleChallenge(entry.text)) {
      return i;
    }
    // Tool messages and assistant text that follow the idle challenge
    // are part of the heartbeat-poll echo; keep walking. We bail out
    // on the first non-heartbeat-tail message we encounter.
    if (entry.role === "assistant" && entry.text) {
      return -1;
    }
  }
  return -1;
}

/**
 * Walk backwards from `tailEndIdx` (the idle-challenge message) over
 * tool calls / tool results / heartbeat-narrative assistant messages
 * and return the index of the first non-heartbeat message — i.e. the
 * message just BEFORE the heartbeat tail block starts. The canonical
 * `finalAnswer` is the last assistant text message before this index.
 *
 * Returns -1 when no heartbeat-tail block was found (the common case
 * where the agent's reply does not include a heartbeat poll).
 */
function findHeartbeatTailStart(
  messages: Array<{ role: string; text: string; message: unknown }>,
  tailEndIdx: number,
): number {
  if (tailEndIdx < 0) {
    return -1;
  }
  let cursor = tailEndIdx;
  // The idle-challenge message itself is part of the tail — step over
  // it (and any consecutive idle-challenge echoes) before walking back.
  while (
    cursor >= 0 &&
    messages[cursor]?.role === "assistant" &&
    isHeartbeatIdleChallenge(messages[cursor]?.text ?? "")
  ) {
    cursor -= 1;
  }
  // Walk back over the heartbeat-poll chain: heartbeat narrative
  // assistant messages, tool calls, tool results.
  while (cursor >= 0) {
    const entry = messages[cursor];
    if (!entry) {
      cursor -= 1;
      continue;
    }
    if (entry.role === "toolresult" || entry.role === "tool" || entry.role === "tool_result") {
      const toolName = getToolNameFromMessage(entry.message).toLowerCase();
      if (HEARTBEAT_POLL_TOOL_NAMES.has(toolName)) {
        cursor -= 1;
        continue;
      }
      // A non-heartbeat tool result means we've walked past the
      // heartbeat-poll region — stop.
      break;
    }
    if (entry.role === "assistant" && entry.text && isHeartbeatTailAssistantText(entry.text)) {
      cursor -= 1;
      continue;
    }
    // Any other message (assistant non-heartbeat, user, etc.) marks the
    // end of the heartbeat-poll tail.
    break;
  }
  // `cursor` now points at the message just before the tail. The tail
  // itself starts at `cursor + 1`.
  return cursor + 1;
}

export type AssistantTurnRenderOptions = {
  assistant?: AssistantIdentity;
  showReasoning: boolean;
  reasoningExpanded?: boolean;
  transcriptMode?: boolean;
  messageActions?: MessageActionHandlers;
  rewindTarget?: MessageRewindTarget | null;
  editingRewindTarget?: MessageRewindTarget | null;
  isLastAssistantGroup?: boolean;
  onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
  onOpenSidebar?: (content: string) => void;
  onOpenViewer?: (markdown: string, messageId: string) => void;
  chatWaitNow?: number;
  inlineFleetQuestion?: unknown;
  /** JAISIU-3551 — set of message ids currently in the manually-collapsed
   * state (same semantics as the user-bubble path). */
  collapsedMessageIds?: ReadonlySet<string>;
  /** JAISIU-3551 — toggle handler for the avatar-driven collapse. */
  onToggleMessageCollapsed?: (messageId: string) => void;
  /** Live-run steps (used when the turn is mid-run). */
  liveSteps?: LiveRunStep[];
  hasActiveTool?: boolean;
  liveRunHint?: string | null;
  activeFileToolPath?: string | null;
  /** Subagent cards (used by the live-run live step builder). */
  subagentsForLive?: Parameters<typeof buildLiveRunSteps>[0]["subagents"];
  compactionStatus?: Parameters<typeof buildLiveRunSteps>[0]["compactionStatus"];
};

/**
 * Lit renderer for the unified `AssistantTurn` group. Mirrors
 * `renderMessageGroup` (avatar + bubble + footer + transcriptMode) but
 * splits the body into a collapsible activity `<details>` and the final
 * answer bubble below it.
 */
export function renderAssistantTurnGroup(
  turn: AssistantTurn,
  opts: AssistantTurnRenderOptions,
): TemplateResult {
  const {
    assistant,
    showReasoning,
    reasoningExpanded,
    transcriptMode,
    isLastAssistantGroup,
    onFileClick,
    onOpenSidebar,
    onOpenViewer,
    chatWaitNow,
    inlineFleetQuestion,
  } = opts;

  const timestamp = new Date(turn.startedAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const name = assistant?.name ?? "Assistant";

  // While the turn is still live we delegate to the existing live-run
  // renderer so the user sees "Working · N steps" + live tool/reasoning
  // stream — the rest of the UI contract (avatar, footer, transcriptMode)
  // is already handled by `renderChatLiveRunGroup`.
  if (turn.isLive) {
    let steps: LiveRunStep[];
    if (opts.liveSteps) {
      steps = opts.liveSteps;
    } else {
      // Maintain a per-turn sticky hint so transient `null` hints from the
      // gateway between phases don't blank the live-run chip
      // (`null → "Reading…" → null` flicker, JAISIU-3153). The memo is
      // keyed by the stable `turn.key` string so it survives
      // `AssistantTurn` rebuilds from `buildAssistantTurns` on every
      // `chat.history` poll (see comment above `liveRunStickyByTurn`).
      const stickyKey = turn.key;
      const prevSticky = liveRunStickyByTurn.get(stickyKey) ?? null;
      // JAISIU-3752 — derive the live thinking summary from the same
      // fields the Inspector activity panel uses. The `lastAssistantThinking`
      // fallback is sourced from the latest assistant message in
      // `turn.activity` so the summary survives a transient clear of the
      // live reasoning stream (e.g. a tool boundary that re-anchors the
      // turn) — the operator keeps seeing the same model message they
      // were tracking until a new one streams in.
      let lastAssistantThinking: string | null = null;
      for (let i = turn.activity.length - 1; i >= 0; i -= 1) {
        const t = extractThinking(turn.activity[i]?.message);
        if (t && t.trim()) {
          lastAssistantThinking = t;
          break;
        }
      }
      const thinkingSummary = resolveLiveThinkingSummary({
        reasoningStream: turn.liveReasoningText,
        stream: turn.liveStreamText,
        lastAssistantThinking,
      });
      const prevStickyThinkingSummary = liveRunThinkingSummaryByTurn.get(stickyKey) ?? null;
      const built = buildLiveRunStepsWithSticky({
        toolMessageCount: turn.activity.length,
        hasActiveTool:
          opts.hasActiveTool ?? hasActiveToolInStream(turn.activity.map((a) => a.message)),
        runActive: true,
        streamText: turn.liveStreamText ?? "",
        reasoningStreamText: turn.liveReasoningText ?? "",
        compactionStatus: opts.compactionStatus ?? null,
        subagents: opts.subagentsForLive ?? [],
        liveRunHint: opts.liveRunHint ?? null,
        stickyLiveRunHint: prevSticky,
        activeFileToolPath: opts.activeFileToolPath ?? null,
        thinkingSummary: thinkingSummary || null,
        stickyThinkingSummary: prevStickyThinkingSummary,
      });
      rememberStickyHint(stickyKey, built.nextStickyLiveRunHint);
      rememberStickyThinkingSummary(stickyKey, built.nextStickyThinkingSummary);
      steps = built.steps;
    }
    return renderChatLiveRunGroup({
      steps,
      streamText: turn.liveStreamText ?? "",
      reasoningStreamText: turn.liveReasoningText ?? null,
      messages: turn.activity.map((a) => a.message),
      startedAt: turn.startedAt,
      waitNowMs: chatWaitNow,
      assistant,
      showReasoning,
      transcriptMode,
      reasoningExpanded,
      onOpenSidebar,
      onOpenViewer,
    });
  }

  const finalAnswerText = turn.finalAnswer ? extractText(turn.finalAnswer)?.trim() : null;
  const hasFinalAnswer = Boolean(finalAnswerText);
  const hasActivity = turn.activity.length > 0;

  // JAISIU-activity-always-visible: open the Activity box by default when
  // the activity is small enough to read at a glance. Without this the
  // operator only sees the "Activity · N steps · Completed" summary
  // collapsed under a tiny caret — the activity "lands at the end" of
  // the bubble instead of filling the space between the run and the
  // final response. The threshold (8 entries) is intentionally small:
  // operators report on multi-step runs, not on 200-row activity logs.
  // For longer runs the disclosure stays collapsed but the caret stays
  // prominent in the summary row so the user can still expand it.
  const ACTIVITY_AUTO_OPEN_MAX = 8;
  // JAISIU-3558 (activity-box always-open for short lists) — the box
  // auto-opens whenever the activity list is short enough that hiding
  // it would be more noise than value. We previously restricted this
  // to `isLive === true` runs (JAISIU-3116 DOM contract) but operators
  // reported that a completed run with 1–3 tool calls rendered a
  // collapsed activity caret that hid the actual work — defeating the
  // point of the disclosure. The contract now opens for any short
  // activity list. A future iteration can introduce a per-message
  // sticky-collapse preference if operators want it.
  const activityAutoOpen = hasActivity && turn.activity.length <= ACTIVITY_AUTO_OPEN_MAX;

  if (!hasActivity && !hasFinalAnswer) {
    return html`<div
      class="chat-group assistant chat-turn--empty"
      data-turn-key=${turn.key}
    ></div>`;
  }

  const activityDurationMs =
    turn.completedAt != null ? Math.max(0, turn.completedAt - turn.startedAt) : 0;
  // Status badge: "Running" while the turn is still active (handled by the
  // live-run renderer above) and "Completed" once it lands. The badge is
  // always present after completion so the user sees an unambiguous end
  // marker even when the activity is collapsed. Duration is appended only
  // for completed turns with measurable wall-clock time to avoid noise on
  // empty / aborted turns.
  const activityLabel = hasActivity
    ? `Activity · ${turn.activity.length} step${turn.activity.length === 1 ? "" : "s"}`
    : "";
  const statusLabel = "Completed";
  const activityDurationLabel =
    hasActivity && activityDurationMs > 0 ? ` · ${formatActivityDuration(activityDurationMs)}` : "";
  const summaryText = hasActivity
    ? `${activityLabel} · ${statusLabel}${activityDurationLabel}`
    : "";

  // JAISIU-3533-update — anchor for scroll-position restoration.
  // `turn.key` is one of three shapes (see `collapseBufferToTurn`):
  //   `turn:<serverId>`     — the turn's final answer has a stable
  //                            server id; the anchor survives across
  //                            polls (the server id is preserved in
  //                            subsequent `chat.history` responses).
  //   `turn:live:<ts>`      — a live-run buffer that has no final
  //                            answer yet (streaming in progress).
  //                            The anchor is the live-run startedAt.
  //   `turn:<ts>`           — completed turn whose final-answer
  //                            record is missing an id (rare; legacy
  //                            transcripts). Anchor is startedAt.
  //
  // The previous condition (`turn.key.startsWith("msg:")`) was a
  // long-standing typo — the renderer was generating `"turn:<id>"`
  // keys from day one, so EVERY assistant row was emitted without
  // a `data-msg-id` attribute and the scroll-restoration path
  // could not anchor against them. That is why a session full of
  // assistant turns had only user rows participating in the
  // capture/restore logic. We now strip the `"turn:"` prefix and
  // fall back to the raw key (without the prefix) when the server
  // id is absent, so EVERY assistant row gets a stable anchor
  // identifier the scroll logic can read back.
  const turnAnchorId = turn.key.startsWith("turn:")
    ? turn.key.slice("turn:".length)
    : turn.key || null;

  return html`
    <div
      class="chat-group assistant chat-turn chat-turn--done"
      data-turn-key=${turn.key}
      data-turn-live="false"
      ?data-last-assistant=${isLastAssistantGroup ? true : nothing}
      data-msg-id=${turnAnchorId ?? nothing}
    >
      ${
        transcriptMode
          ? nothing
          : renderAvatar(
              "assistant",
              assistant,
              // JAISIU-3551 — assistant bubble is collapsible too. The
              // toggle is gated by:
              //   1. The turn must NOT be live (live state is shown via
              //      the live-run renderer; collapsing it would hide the
              //      streaming indicators).
              //   2. There must be a stable id on `turn.finalAnswerId`
              //      (most assistant rows have one; rare legacy rows
              //      without still skip the toggle).
              //   3. The final-answer body must be long enough to be
              //      worth collapsing (same one-liner guard as the user
              //      bubble path).
              !turn.isLive &&
                turn.finalAnswerId &&
                opts.onToggleMessageCollapsed &&
                isCollapsibleText(extractText(turn.finalAnswer ?? undefined))
                ? {
                    collapseToggleId: turn.finalAnswerId,
                    onCollapseToggle: opts.onToggleMessageCollapsed,
                  }
                : undefined,
            )
      }
      <div class="chat-group-messages">
        ${
          hasActivity
            ? html`
                <details
                  class="chat-turn__activity ${activityAutoOpen ? "chat-turn__activity--open" : ""}"
                  data-turn-activity
                  data-testid="chat-turn-activity"
                  ?open=${activityAutoOpen ? true : nothing}
                >
                  <summary
                    class="chat-turn__activity-summary"
                    data-testid="chat-turn-activity-summary"
                  >
                    <span class="chat-turn__activity-caret" aria-hidden="true"
                      >${activityAutoOpen ? "▾" : "▸"}</span
                    >
                    <span
                      class="chat-turn__activity-label-text"
                      data-testid="chat-turn-activity-label"
                      >${activityLabel}${activityDurationLabel}</span
                    >
                    <span class="chat-turn__activity-status" data-testid="chat-turn-activity-status"
                      >${statusLabel}</span
                    >
                  </summary>
                  <div class="chat-turn__activity-body" data-turn-activity-body>
                    ${turn.activity.map(
                      (entry, idx) => html`
                        <div
                          class="chat-turn__activity-row ${
                            idx === turn.activity.length - 1 && turn.isLive && entry.active
                              ? "chat-turn__activity-row--active"
                              : ""
                          }"
                          data-turn-activity-id=${entry.id}
                        >
                          <span class="chat-turn__activity-icon" aria-hidden="true">
                            ${entry.active ? icons.loader : icons.check}
                          </span>
                          <div class="chat-turn__activity-text">
                            <div class="chat-turn__activity-label">${entry.label}</div>
                            ${
                              entry.detail
                                ? html`<div class="chat-turn__activity-detail muted">
                                    ${entry.detail}
                                  </div>`
                                : nothing
                            }
                            ${
                              entry.detail || transcriptMode
                                ? renderActivityEntryMessage(entry, {
                                    onOpenSidebar,
                                    onOpenViewer,
                                    transcriptMode: transcriptMode === true,
                                  })
                                : nothing
                            }
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                </details>
              `
            : nothing
        }
        ${
          hasFinalAnswer && turn.finalAnswer
            ? renderGroupedMessage(
                turn.finalAnswer,
                {
                  isStreaming: false,
                  showReasoning,
                  reasoningExpanded,
                  transcriptMode,
                  messageActions: opts.messageActions,
                  rewindTarget: opts.rewindTarget ?? null,
                  editingRewindTarget: opts.editingRewindTarget ?? null,
                  isLastAssistantInThread: Boolean(isLastAssistantGroup),
                  onFileClick,
                  onOpenViewer,
                  // JAISIU-3551 — forward collapse state so the final-answer
                  // bubble renders the one-line preview when the operator
                  // has collapsed the assistant turn via the avatar click.
                  collapsedMessageIds: opts.collapsedMessageIds,
                  onToggleMessageCollapsed: opts.onToggleMessageCollapsed,
                },
                onOpenSidebar,
              )
            : nothing
        }
        ${
          isLastAssistantGroup && !transcriptMode && inlineFleetQuestion
            ? inlineFleetQuestion
            : nothing
        }
        ${
          transcriptMode
            ? nothing
            : html`
                <div class="chat-group-footer">
                  <span class="chat-sender-name">${name}</span>
                  <span class="chat-group-timestamp">${timestamp}</span>
                  ${
                    activityDurationMs > 0 && !turn.isLive
                      ? html`<span class="chat-turn__duration muted" aria-label="Turn duration"
                          >${formatActivityDuration(activityDurationMs)}</span
                        >`
                      : nothing
                  }
                </div>
              `
        }
      </div>
    </div>
  `;
}

/**
 * Render the underlying message for an activity row. We use the regular
 * `renderGroupedMessage` so tool cards keep working, but strip the avatar
 * and message-action UI to keep the row compact.
 */
function renderActivityEntryMessage(
  entry: AssistantTurnActivityEntry,
  opts: {
    onOpenSidebar?: (content: string) => void;
    onOpenViewer?: (markdown: string, messageId: string) => void;
    transcriptMode: boolean;
  },
): TemplateResult | typeof nothing {
  if (!entry.message || typeof entry.message !== "object") {
    return nothing;
  }
  return html`<div class="chat-turn__activity-message">
    ${renderGroupedMessage(
      entry.message,
      {
        isStreaming: false,
        showReasoning: false,
        transcriptMode: opts.transcriptMode,
        onOpenViewer: opts.onOpenViewer,
        inlineInRun: true,
      },
      opts.onOpenSidebar,
    )}
  </div>`;
}

/** "2.3s" / "1m 12s" formatter for the activity badge. */
export function formatActivityDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) {
    return "0s";
  }
  if (ms < 1000) {
    return `${Math.max(1, Math.round(ms))}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds - minutes * 60);
  return `${minutes}m ${remainder}s`;
}

// Re-export `formatChatWaitElapsed` so callers wiring the live renderer
// don't need to import it from chat-live-run.ts separately.
export { formatChatWaitElapsed };
